/**
 * Client for the laptop fleet screens (release 2.1 Gate B items 14-18).
 * Wire shapes mirror apps/api-controller/src/modules/discovery/fleet/*.
 * Kept out of the 7,000-line api.ts so parallel work on that file does not
 * collide with this one.
 */

import { apiFetch } from './api';

export type BuFilter = 'unassigned' | string;

export type CoverageState =
  | 'scanned'
  | 'scanned_with_skips'
  | 'failed'
  | 'paused'
  | 'not_attempted'
  | 'silent'
  | 'stale'
  | 'never_reported';

export type LivenessState = 'reporting' | 'silent' | 'stale' | 'never_reported';

export interface Freshness {
  asOf: string;
  /** When the sums were counted: fleet-wide totals are shared for a few seconds. */
  countedAt?: string;
  /** False for a user limited to some business units: the queue is not theirs to see. */
  visible: boolean;
  pendingDevices: number;
  lagSeconds: number;
  lastRecomputeAt: string | null;
  /** Laptop names with more than `oversizeLimit` findings each, left out of every count. */
  oversizeDevices?: number;
  oversizeLimit?: number;
}

export interface FleetCoverage {
  windowHours: number;
  laptops: number;
  scanned: number;
  scannedClean: number;
  scannedWithSkips: number;
  coveragePct: number | null;
  states: Record<CoverageState, number>;
  liveness: Record<LivenessState, number>;
  asOf: string;
}

export interface CoverageException {
  podRowId: string;
  podId: string;
  label: string;
  hostname: string | null;
  businessUnitId: string | null;
  businessUnitName: string | null;
  state: CoverageState;
  reason: string;
  lastHeartbeatAt: string | null;
  lastScanFinishedAt: string | null;
  lastScanOutcome: string | null;
  targetsFailed: number | null;
  filesNotRead: number | null;
  version: string;
}

export interface CoverageExceptionPage {
  items: CoverageException[];
  nextCursor: string | null;
  total: number;
  windowHours: number;
  asOf: string;
}

export interface PodInfo {
  pods: number;
  hostname: string | null;
  lastHeartbeatAt: string | null;
  lastScanFinishedAt: string | null;
  lastScanOutcome: string | null;
  liveness: LivenessState | null;
}

export interface RiskContribution {
  dataType: string;
  severity: string;
  files: number;
  points: number;
}

export interface TopDevice {
  deviceKey: string;
  businessUnitId: string | null;
  businessUnitName: string | null;
  riskScore: number;
  riskExplain: RiskContribution[];
  topTypes: string[];
  openFindings: number;
  openFiles: number;
  maxSeverity: number | null;
  deviceUser: string | null;
  lastFindingAt: string | null;
  pod: PodInfo | null;
}

export interface TopType {
  dataType: string;
  openFiles: number;
  openFindings: number;
  devices: number;
  maxSeverity: number;
}

export interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

/**
 * Laptop counts. Release 3.1 added the fleet-wide vocabulary (the same words
 * and thresholds as Edge Fleet): healthy, degraded, silent, down, pending.
 * They are optional because a controller before 3.1 sends only the older
 * names, which are still sent and mean: reporting = healthy + degraded,
 * stale = down, neverReported = pending.
 */
export interface FleetLaptopCounts {
  enrolled: number;
  healthy?: number;
  degraded?: number;
  silent: number;
  down?: number;
  pending?: number;
  reporting: number;
  stale: number;
  neverReported: number;
}

export interface FleetSummary {
  laptops: FleetLaptopCounts;
  coverage: {
    windowHours: number;
    laptops: number;
    scanned: number;
    scannedWithSkips: number;
    failed: number;
    coveragePct: number | null;
  };
  findings: {
    devicesWithFindings: number;
    devicesWithOpenFindings: number;
    openFindings: number;
    openFiles: number;
    bySeverity: SeverityCounts;
    filesByWorst: SeverityCounts;
    reviewOpen: number;
    weakOpen: number;
    typeCount: number;
  };
  topDevices: TopDevice[];
  topTypes: TopType[];
  riskFormula: string;
  openDefinition: string;
  freshness: Freshness;
}

export interface TrendPoint {
  day: string;
  enrolled: number;
  reporting: number;
  silent: number;
  stale: number;
  neverReported: number;
  scanned24h: number;
  scannedWithSkips24h: number;
  failed24h: number;
  notAttempted24h: number;
  devicesWithFindings: number;
  openFindings: number;
  openFiles: number;
  critFindings: number;
  highFindings: number;
  snapshotAt: string | null;
}

export interface TreeTotals {
  openFindings: number;
  openFiles: number;
  maxSeverity: string | null;
  devices?: number;
}

export interface TreeItem {
  id: string;
  kind: 'bu' | 'device' | 'folder' | 'file';
  label: string;
  hasChildren: boolean;
  totals: TreeTotals;
  businessUnitId?: string | null;
  riskScore?: number;
  riskExplain?: RiskContribution[];
  topTypes?: string[];
  deviceUser?: string | null;
  pod?: PodInfo | null;
  file?: { assetId: string; ref: string; types: string[]; totalFindings: number; lastSeenAt: string | null };
}

export interface TreePage {
  node: { id: string; kind: 'fleet' | 'bu' | 'device' | 'folder'; totals: TreeTotals | null };
  items: TreeItem[];
  nextCursor: string | null;
  total: number;
  filtered: boolean;
  freshness: Freshness;
}

export interface TreeQuery {
  node: string;
  dataType?: string | undefined;
  minSeverity?: 'critical' | 'high' | 'medium' | 'low' | 'info' | undefined;
  onlyOpen?: boolean;
  cursor?: string | undefined;
  limit?: number;
}

export interface LineageNode {
  id: string;
  kind: 'bu' | 'device' | 'type' | 'folder' | 'others';
  column: 0 | 1 | 2;
  label: string;
  value: number;
  drill?: string;
  count?: number;
  maxSeverity?: string | null;
}

export interface LineageGraph {
  scope: string;
  scopeKind: 'fleet' | 'bu' | 'device';
  metric: 'files' | 'findings';
  nodes: LineageNode[];
  edges: Array<{ source: string; target: string; value: number }>;
  truncated: boolean;
  omitted: { bus: number; devices: number; types: number; folders: number };
  maxNodes: number;
  freshness: Freshness;
}

export interface LineageHeatmap {
  metric: 'files' | 'findings';
  rows: Array<{ id: string | null; label: string; drill?: string; total: number; count?: number }>;
  columns: Array<{ dataType: string; total: number; count?: number }>;
  cells: number[][];
  omitted: { bus: number; types: number };
  freshness: Freshness;
}

export interface FindingCounts {
  open: SeverityCounts & { total: number };
  unresolved: SeverityCounts & { total: number };
  /** status -> severity -> count; laptop findings from the rollups, the rest counted directly. */
  byStatus: {
    laptops: Record<string, Record<string, number>>;
    other: Record<string, Record<string, number>>;
  };
  freshness: Freshness;
}

export interface PodCounts {
  /** Pods in service (not decommissioned). */
  total: number;
  /**
   * Release 3.1: the one vocabulary (healthy, degraded, silent, down,
   * pending), plus `stale` as the pre-3.1 alias of `silent`.
   */
  byHealth: Record<string, number>;
  byComponent: Record<string, number>;
  /** Decommissioned ("Retired") pods, counted apart from `total`. */
  retired?: number;
  asOf: string;
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function getFleetCoverage(p: { bu?: BuFilter; windowHours?: number } = {}): Promise<FleetCoverage> {
  return apiFetch(`/v1/discovery/fleet/coverage${qs(p)}`);
}

export function getFleetExceptions(
  p: { bu?: BuFilter; windowHours?: number; state?: CoverageState; cursor?: string; limit?: number } = {},
): Promise<CoverageExceptionPage> {
  return apiFetch(`/v1/discovery/fleet/coverage/exceptions${qs(p)}`);
}

/** Release 8, C4: one store (a laptop or a server scanner) with files it could not read. */
export interface UnreadableStore {
  podRowId: string;
  podId: string;
  label: string;
  hostname: string | null;
  kind: 'laptop' | 'server_scanner';
  businessUnitId: string | null;
  businessUnitName: string | null;
  agentVersion: string | null;
  lastScanFinishedAt: string | null;
  filesRead: number | null;
  filesNotRead: number | null;
  /** True when this agent separates encrypted files from damaged ones. */
  separatesEncrypted: boolean;
  /** null = this agent cannot say; never zero. */
  encryptedFiles: number | null;
  encryptedTypes: Array<{ ext: string; count: number }>;
  encryptedExamples: string[];
  couldNotOpenFiles: number | null;
}

export interface UnreadableStoresReport {
  asOf: string;
  totals: {
    stores: number;
    storesReported: number;
    storesSeparatingEncrypted: number;
    storesWithEncrypted: number;
    encryptedFiles: number;
    storesNotSeparatingWithUnopened: number;
  };
  items: UnreadableStore[];
  truncated: boolean;
  basis: string;
  /** When the (briefly cached) figures were counted. */
  countedAt?: string;
}

export function getUnreadableStores(p: { bu?: BuFilter; limit?: number } = {}): Promise<UnreadableStoresReport> {
  return apiFetch(`/v1/discovery/fleet/unreadable${qs(p)}`);
}

export function getFleetSummary(p: { bu?: BuFilter } = {}): Promise<FleetSummary> {
  return apiFetch(`/v1/discovery/fleet/summary${qs(p)}`);
}

export function getFleetTrend(p: { bu?: BuFilter; days?: number } = {}): Promise<{ days: TrendPoint[]; dayBoundary: string; asOf: string }> {
  return apiFetch(`/v1/discovery/fleet/trend${qs(p)}`);
}

export function getInventoryTree(p: TreeQuery): Promise<TreePage> {
  return apiFetch(
    `/v1/discovery/inventory/tree${qs({
      node: p.node,
      dataType: p.dataType,
      minSeverity: p.minSeverity,
      onlyOpen: p.onlyOpen === undefined ? undefined : String(p.onlyOpen),
      cursor: p.cursor,
      limit: p.limit,
    })}`,
  );
}

export function getFleetLineage(p: { scope?: string; metric?: 'files' | 'findings'; maxNodes?: number } = {}): Promise<LineageGraph> {
  return apiFetch(`/v1/discovery/lineage/fleet${qs(p)}`);
}

export function getFleetHeatmap(p: { metric?: 'files' | 'findings' } = {}): Promise<LineageHeatmap> {
  return apiFetch(`/v1/discovery/lineage/fleet/heatmap${qs(p)}`);
}

export function getFindingCounts(): Promise<FindingCounts> {
  return apiFetch('/v1/discovery/findings/counts');
}

export function getPodCounts(p: { component?: 'discovery' | 'activity' | 'detection' } = {}): Promise<PodCounts> {
  return apiFetch(`/v1/discovery/fleet/pod-counts${qs(p)}`);
}
